import { Injectable } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import {
    DatiqGraphAxisDateTypes,
    IDatiqGraph,
    IDatiqGraphAxis,
} from '../models';
import { DatiqAmcharts5AxisUtilsService } from './datiq-amcharts-5-axis-utils.service';
import { noop } from 'lodash';
import {
    datGrey,
    datiqFontFamilyCaption,
} from '@dat/datiq-components-pkg/utils';
import { DatiqAmcharts5UtilsService } from './datiq-amcharts-5-utils.service';

@Injectable({
    providedIn: 'root',
})
export class DatiqAmcharts5AxisDateService {
    constructor(
        private _utils: DatiqAmcharts5AxisUtilsService,
        private _amUtil: DatiqAmcharts5UtilsService
    ) {}
    createXAxis(
        root: am5.Root,
        chart: am5xy.XYChart,
        axis: IDatiqGraphAxis,
        settings: IDatiqGraph
    ): am5xy.DateAxis<am5xy.AxisRendererX> {
        const renderer = this._utils.setXAxisRenderer(root, axis);

        const axisX: am5xy.DateAxis<am5xy.AxisRendererX> = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                baseInterval: this.setAxisTimeInterval(axis),
                markUnitChange: this.setMarkUnitChange(axis),
                gridIntervals: this.createGridIntervals(axis),
                renderer,
            })
        );

        axisX.set(
            'tooltip',
            am5.Tooltip.new(root, {
                themeTags: ['axis'],
                forceHidden: true,
            })
        );

        this._utils
            .setUpXAxisRenderer(renderer, settings)
            .setUpXAxis(axisX)
            .setUpXAxisLabel(axisX, axis, root);
        this.addAxisToolTip(root, axis, axisX);

        return this.setDateFormatsX(axis, axisX)
            .setGridLabelFormatting(renderer)
            .buildXAxis(axisX);
    }
    addAxisToolTip(
        root: am5.Root,
        axis: IDatiqGraphAxis,
        axisX: am5xy.DateAxis<am5xy.AxisRendererX>
    ): this {
        const tooltip = am5.Tooltip.new(root, {
            rotation: 60,
            paddingBottom: 4,
            paddingTop: 4,
            paddingLeft: 4,
            paddingRight: 4,
            layer: 0,
        });

        tooltip.get('background').setAll({
            fill: this._amUtil.setColor(datGrey['200']),
            layer: 0,
        });

        tooltip.label.setAll({
            fontSize: datiqFontFamilyCaption.fontSize,
            fontWeight: datiqFontFamilyCaption.fontWeight,
        });

        return this;
    }

    setMarkUnitChange(axis: IDatiqGraphAxis): boolean {
        // Returning true for now just in case we need to add logic for grouping later
        return false;
    }

    setGridLabelFormatting(renderer: am5xy.AxisRendererX): this {
        this._utils.setDefaultLabelStyles(renderer);
        renderer.labels.template.setAll({
            rotation: 60,
            dx: 16,
        });

        return this;
    }
    private createGridIntervals(
        axis: IDatiqGraphAxis
    ): am5.time.ITimeInterval[] {
        return this.isDaily(axis)
            ? [{ timeUnit: 'day', count: 1 }]
            : this.isWeekly(axis)
            ? [{ timeUnit: 'week', count: 1 }]
            : this.isMonthly(axis)
            ? [{ timeUnit: 'month', count: 1 }]
            : this.isYearly(axis)
            ? [{ timeUnit: 'year', count: 1 }]
            : this.isBiYearly(axis)
            ? [{ timeUnit: 'month', count: 6 }]
            : [{ timeUnit: 'day', count: 1 }];
    }

    setDateFormatsX(
        axis: IDatiqGraphAxis,
        axisX: am5xy.DateAxis<am5xy.AxisRendererX>
    ): this {
        this.isDaily(axis)
            ? this.setupDailyLabel(axisX)
            : this.isWeekly(axis)
            ? this.setupWeeklyLabel(axisX)
            : this.isMonthly(axis)
            ? this.setupMonthlyLabel(axisX)
            : this.isYearly(axis)
            ? this.setupYearlyLabel(axisX)
            : this.isBiYearly(axis)
            ? this.setupBiYearlyLabel(axisX)
            : this.isMonthlyWithoutYear(axis)
            ? this.setupMonthlyLabelWithoutYear(axisX)
            : noop();

        return this;
    }
    isMonthlyWithoutYear(axis: IDatiqGraphAxis): boolean {
        return axis?.dateAxisType === DatiqGraphAxisDateTypes.MonthlyNoYear;
    }

    private setupDailyLabel(axisX: am5xy.DateAxis<am5xy.AxisRendererX>) {
        axisX.set('dateFormats', {
            day: 'MM/dd/yy',
            week: 'MM/dd/yy',
            month: 'MM/dd/yy',
            year: `yyyy`,
        });
    }
    private setupWeeklyLabel(axisX: am5xy.DateAxis<am5xy.AxisRendererX>) {
        axisX.set('dateFormats', {
            day: 'MM/dd/yy',
            week: 'MM/dd/yy',
            month: 'MM/dd/yy',
            year: `yyyy`,
        });
    }
    private setupMonthlyLabel(axisX: am5xy.DateAxis<am5xy.AxisRendererX>) {
        axisX.set('dateFormats', {
            day: 'MM/dd/yy',
            week: 'MM/dd/yy',
            month: `MMM \`yy`,
            year: `MMM dd \`yy`,
        });
    }
    private setupMonthlyLabelWithoutYear(
        axisX: am5xy.DateAxis<am5xy.AxisRendererX>
    ) {
        axisX.set('dateFormats', {
            day: 'MM/dd/yy',
            week: 'MM/dd/yy',
            month: `MMM`,
            year: `MMM dd \`yy`,
        });
    }
    private setupYearlyLabel(axisX: am5xy.DateAxis<am5xy.AxisRendererX>) {
        axisX.set('dateFormats', {
            year: `yyyy`,
        });
    }
    private setupBiYearlyLabel(axisX: am5xy.DateAxis<am5xy.AxisRendererX>) {
        axisX.set('dateFormats', {
            month: `MMM \`yy`,
        });
    }

    buildXAxis(
        axisX: am5xy.DateAxis<am5xy.AxisRendererX>
    ): am5xy.DateAxis<am5xy.AxisRendererX> {
        return axisX;
    }
    createYAxis(
        root: am5.Root,
        chart: am5xy.XYChart,
        axis: IDatiqGraphAxis,
        settings: IDatiqGraph
    ): am5xy.DateAxis<am5xy.AxisRendererY> {
        const renderer = this._utils.setYAxisRenderer(root, axis);
        const axisY: am5xy.DateAxis<am5xy.AxisRendererY> = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                baseInterval: this.setAxisTimeInterval(axis),
                renderer,
            })
        );

        this._utils.setUpYAxisRenderer(renderer).setUpYAxis(axisY);

        return this.buildYAxis(axisY);
    }

    buildYAxis(
        axisY: am5xy.DateAxis<am5xy.AxisRendererY>
    ): am5xy.DateAxis<am5xy.AxisRendererY> {
        return axisY;
    }

    setAxisTimeInterval(axis: IDatiqGraphAxis): am5.time.ITimeInterval {
        return this.isDailyValue(axis)
            ? { timeUnit: 'day', count: 1 }
            : this.isWeeklyValue(axis)
            ? { timeUnit: 'week', count: 1 }
            : this.isMonthlyValue(axis)
            ? { timeUnit: 'month', count: 1 }
            : this.isYearlyValue(axis)
            ? { timeUnit: 'year', count: 1 }
            : this.isBiYearlyValue(axis)
            ? { timeUnit: 'month', count: 1 }
            : { timeUnit: 'day', count: 1 };
    }

    private isMonthly(axis: IDatiqGraphAxis): boolean {
        return axis.dateAxisType === DatiqGraphAxisDateTypes.Monthly;
    }

    private isWeekly(axis: IDatiqGraphAxis): boolean {
        return axis.dateAxisType === DatiqGraphAxisDateTypes.Weekly;
    }

    private isDaily(axis: IDatiqGraphAxis): boolean {
        return axis.dateAxisType === DatiqGraphAxisDateTypes.Daily;
    }
    private isYearly(axis: IDatiqGraphAxis): boolean {
        return axis.dateAxisType === DatiqGraphAxisDateTypes.Yearly;
    }
    private isBiYearly(axis: IDatiqGraphAxis): boolean {
        return axis.dateAxisType === DatiqGraphAxisDateTypes.BiYearly;
    }
    private isMonthlyValue(axis: IDatiqGraphAxis): boolean {
        return axis.dateValueType === DatiqGraphAxisDateTypes.Monthly;
    }

    private isWeeklyValue(axis: IDatiqGraphAxis): boolean {
        return axis.dateValueType === DatiqGraphAxisDateTypes.Weekly;
    }

    private isDailyValue(axis: IDatiqGraphAxis): boolean {
        return axis.dateValueType === DatiqGraphAxisDateTypes.Daily;
    }
    private isYearlyValue(axis: IDatiqGraphAxis): boolean {
        return axis.dateValueType === DatiqGraphAxisDateTypes.Yearly;
    }
    private isBiYearlyValue(axis: IDatiqGraphAxis): boolean {
        return axis.dateValueType === DatiqGraphAxisDateTypes.BiYearly;
    }
}
